import { useState, useEffect, useCallback, createContext, useContext, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Image, Video, ChevronDown, ChevronRight, Check, Loader2, Sparkles, Palette, Camera, Gamepad2, PenTool, Brush, Film, BookOpen, RefreshCw, Search, CheckCircle2, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import nexisLogo from "@/assets/nexis-logo-new.png";
import { NexisBreadcrumb } from "@/components/nexis/NexisBreadcrumb";

// Progress tracking context
interface ProgressContextType {
  loadedCount: number;
  totalCount: number;
  markLoaded: (styleName: string) => void;
}

const ProgressContext = createContext<ProgressContextType>({
  loadedCount: 0,
  totalCount: 0,
  markLoaded: () => {},
});

const PLATFORMS = ["TikTok", "YouTube Shorts", "Instagram Reels", "YouTube Long-form"];

// Supabase storage URL for style previews
const STORAGE_URL = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/style-previews`;

// Helper to get storage URL for a style
const getStylePreviewUrl = (styleName: string): string => {
  const fileName = styleName.toLowerCase().replace(/[^a-z0-9]/g, "-");
  return `${STORAGE_URL}/${fileName}.png`;
};

// Fallback placeholder gradient based on style
const getPlaceholderGradient = (gradient: string): string => {
  return `bg-gradient-to-br ${gradient}`;
};

// Style categories with icons, gradient colors, and styles
const STYLE_CATEGORIES: Record<string, { icon: any; gradient: string; styles: { name: string; gradient: string }[] }> = {
  "Vibrant Fantasy": {
    icon: Sparkles,
    gradient: "from-purple-500 via-pink-500 to-orange-400",
    styles: [
      { name: "80s Retro", gradient: "from-pink-500 to-purple-600" },
      { name: "Psychedelic", gradient: "from-green-400 to-yellow-500" },
      { name: "Light Drawings", gradient: "from-pink-400 to-purple-500" },
      { name: "Synthwave", gradient: "from-cyan-400 to-pink-500" },
      { name: "Neon Cyberpunk", gradient: "from-cyan-400 to-pink-500" },
      { name: "Aurora Borealis", gradient: "from-green-400 via-cyan-400 to-purple-500" },
      { name: "Cosmic Nebula", gradient: "from-purple-600 to-blue-800" },
      { name: "Crystal Cave", gradient: "from-cyan-300 to-blue-500" },
      { name: "Bioluminescent Ocean", gradient: "from-cyan-500 to-blue-700" },
      { name: "Steampunk City", gradient: "from-amber-600 to-orange-800" },
    ],
  },
  "Art Media": {
    icon: Brush,
    gradient: "from-amber-400 via-orange-500 to-red-500",
    styles: [
      { name: "Digital Watercolor", gradient: "from-blue-300 via-pink-200 to-yellow-200" },
      { name: "Gouache Watercolor", gradient: "from-pink-300 to-orange-300" },
      { name: "Nozen Watercolor", gradient: "from-yellow-300 to-pink-400" },
      { name: "Watercolor Ink", gradient: "from-purple-400 to-pink-400" },
      { name: "Oil Paint", gradient: "from-amber-600 to-orange-700" },
      { name: "Oil Paint Impasto", gradient: "from-blue-500 to-yellow-500" },
      { name: "Collage", gradient: "from-orange-400 via-pink-400 to-blue-400" },
      { name: "Graffiti Style", gradient: "from-pink-500 to-orange-500" },
      { name: "Traditional Chinese", gradient: "from-gray-500 to-amber-500" },
      { name: "Chinese Ink", gradient: "from-gray-600 to-gray-800" },
      { name: "Ink", gradient: "from-gray-700 to-black" },
      { name: "Ink and Wash", gradient: "from-yellow-400 to-gray-500" },
      { name: "Charcoal", gradient: "from-gray-600 to-gray-800" },
      { name: "Fresco", gradient: "from-amber-500 to-orange-600" },
      { name: "Crayon by Children", gradient: "from-yellow-400 to-green-400" },
      { name: "Ballpoint", gradient: "from-blue-400 to-blue-600" },
      { name: "Sharpie Drawing", gradient: "from-gray-800 to-black" },
      { name: "Pencil Sketch", gradient: "from-gray-300 to-gray-500" },
      { name: "Caricature Sketch", gradient: "from-gray-400 to-gray-600" },
      { name: "Pastel Sketch", gradient: "from-pink-200 via-blue-200 to-yellow-200" },
      { name: "Wool Felt", gradient: "from-orange-400 to-green-400" },
      { name: "Papercut", gradient: "from-orange-500 to-yellow-500" },
      { name: "Claymation", gradient: "from-orange-400 to-amber-600" },
      { name: "Vintage Dolls", gradient: "from-gray-500 to-green-600" },
      { name: "Paper Toy", gradient: "from-cyan-400 to-pink-400" },
      { name: "Woodcut Medieval", gradient: "from-amber-700 to-orange-900" },
      { name: "Linocut", gradient: "from-gray-700 to-black" },
      { name: "Etching", gradient: "from-amber-500 to-gray-600" },
      { name: "Stencil Art", gradient: "from-gray-600 to-pink-500" },
      { name: "Risograph", gradient: "from-pink-500 to-cyan-500" },
      { name: "Porcelain", gradient: "from-gray-200 to-gray-400" },
      { name: "Stained Glass", gradient: "from-red-500 via-blue-500 to-green-500" },
      { name: "Glass Sculpture", gradient: "from-cyan-400 to-orange-400" },
    ],
  },
  "Art Style": {
    icon: Palette,
    gradient: "from-rose-400 via-purple-500 to-indigo-500",
    styles: [
      { name: "Palette Knife Impressionism", gradient: "from-orange-500 to-yellow-500" },
      { name: "Impressionism Heavy Impasto", gradient: "from-blue-400 via-pink-400 to-yellow-400" },
      { name: "Mary Cassatt", gradient: "from-pink-300 to-blue-300" },
      { name: "Pointillism", gradient: "from-blue-400 to-green-400" },
      { name: "Piero della Francesca", gradient: "from-blue-500 to-amber-500" },
      { name: "Medieval", gradient: "from-amber-600 to-blue-600" },
      { name: "Northern Renaissance", gradient: "from-amber-500 to-red-600" },
      { name: "Alphonse Mucha", gradient: "from-amber-400 to-green-500" },
      { name: "Renaissance", gradient: "from-amber-500 to-orange-700" },
      { name: "Rococo", gradient: "from-pink-300 to-blue-300" },
      { name: "Rembrandt", gradient: "from-amber-600 to-gray-700" },
      { name: "Botticelli", gradient: "from-blue-400 to-orange-400" },
      { name: "Roman Sculpture", gradient: "from-gray-400 to-gray-600" },
      { name: "Rodin Bronze Sculpture", gradient: "from-amber-600 to-gray-600" },
      { name: "Ukiyo-e", gradient: "from-blue-500 to-red-400" },
      { name: "European Naive Folk", gradient: "from-blue-400 to-orange-400" },
      { name: "Dali Surrealism", gradient: "from-amber-500 to-blue-500" },
      { name: "Expressionism", gradient: "from-blue-600 to-orange-600" },
      { name: "Fauvism", gradient: "from-yellow-500 to-blue-500" },
      { name: "Pop Art", gradient: "from-yellow-400 via-pink-500 to-cyan-500" },
      { name: "Minimalistic", gradient: "from-gray-200 to-pink-200" },
      { name: "Abstract Glass", gradient: "from-purple-500 to-pink-500" },
      { name: "Neon Rhythms", gradient: "from-cyan-500 to-pink-500" },
    ],
  },
  "Photography": {
    icon: Camera,
    gradient: "from-gray-400 via-gray-600 to-gray-800",
    styles: [
      { name: "Photorealistic Portrait", gradient: "from-gray-400 to-gray-600" },
      { name: "Bokeh", gradient: "from-amber-300 to-orange-400" },
      { name: "Glamour Photography", gradient: "from-pink-400 to-purple-500" },
      { name: "Analog Photography", gradient: "from-teal-500 to-green-500" },
      { name: "Holga Photography", gradient: "from-amber-400 to-pink-400" },
      { name: "Dramatic Black and White", gradient: "from-gray-300 to-gray-700" },
      { name: "Film Noir", gradient: "from-gray-700 to-black" },
      { name: "Infrared", gradient: "from-gray-300 to-white" },
      { name: "Motion (Long Exposure)", gradient: "from-pink-300 to-gray-400" },
      { name: "Silhouettes", gradient: "from-orange-400 to-gray-700" },
      { name: "Nature Close-up", gradient: "from-pink-300 to-white" },
      { name: "Macro", gradient: "from-pink-400 to-purple-400" },
      { name: "Polaroid", gradient: "from-amber-200 to-orange-300" },
      { name: "Very Old Photos", gradient: "from-amber-400 to-gray-500" },
      { name: "Backlit", gradient: "from-orange-400 to-gray-600" },
    ],
  },
  "Gaming": {
    icon: Gamepad2,
    gradient: "from-green-400 via-cyan-500 to-blue-600",
    styles: [
      { name: "Pixel Art", gradient: "from-green-500 to-purple-500" },
      { name: "Voxel", gradient: "from-blue-500 to-green-500" },
      { name: "Low Poly", gradient: "from-teal-400 to-blue-500" },
      { name: "GTA Cover Art", gradient: "from-orange-500 to-pink-500" },
      { name: "Game Screens", gradient: "from-amber-500 to-gray-500" },
      { name: "80s Game Box-cover", gradient: "from-cyan-500 to-pink-500" },
      { name: "Semi-photorealistic Scenes", gradient: "from-green-500 to-blue-500" },
      { name: "Battle Maps", gradient: "from-green-600 to-amber-500" },
      { name: "Miniature Worlds", gradient: "from-cyan-400 to-blue-500" },
      { name: "Miniature Scenes Aerial View", gradient: "from-gray-400 to-blue-400" },
    ],
  },
  "Anime": {
    icon: Film,
    gradient: "from-pink-500 via-red-400 to-orange-300",
    styles: [
      { name: "General Anime Style", gradient: "from-pink-400 to-purple-400" },
      { name: "ToonYou", gradient: "from-red-400 to-pink-400" },
      { name: "Cute Anime Style", gradient: "from-pink-300 to-white" },
      { name: "Studio Ghibli", gradient: "from-sky-400 to-green-400" },
      { name: "1990s Anime", gradient: "from-blue-500 to-gray-500" },
      { name: "J-Horror", gradient: "from-gray-700 to-red-900" },
      { name: "Chibi Doll", gradient: "from-pink-300 to-blue-300" },
      { name: "Kawaii Chibi", gradient: "from-green-400 to-pink-400" },
      { name: "Cyberpunk Anime", gradient: "from-purple-500 to-cyan-400" },
      { name: "Mecha Anime", gradient: "from-red-600 to-gray-600" },
    ],
  },
  "Comic & Cartoon": {
    icon: BookOpen,
    gradient: "from-yellow-400 via-red-500 to-blue-500",
    styles: [
      { name: "American Comic Sketch #1", gradient: "from-purple-500 to-orange-400" },
      { name: "American Comic Sketch #2", gradient: "from-gray-600 to-gray-800" },
      { name: "World of Tintin", gradient: "from-sky-400 to-green-400" },
      { name: "Caricatures", gradient: "from-blue-400 to-purple-400" },
      { name: "Clean Comic", gradient: "from-gray-300 to-gray-500" },
      { name: "Retro Comic", gradient: "from-amber-500 to-orange-500" },
      { name: "Soviet Retro-futurism", gradient: "from-amber-600 to-red-600" },
      { name: "Jack Kirby", gradient: "from-green-500 to-amber-500" },
      { name: "Early 30s", gradient: "from-yellow-400 to-orange-400" },
      { name: "Lego", gradient: "from-red-500 to-yellow-500" },
      { name: "Smurfed", gradient: "from-blue-500 to-blue-600" },
      { name: "Shrek-o-matic", gradient: "from-green-500 to-amber-500" },
      { name: "Simpsonized", gradient: "from-yellow-400 to-blue-400" },
    ],
  },
  "Illustration": {
    icon: PenTool,
    gradient: "from-teal-400 via-blue-500 to-purple-500",
    styles: [
      { name: "Simple Line Art", gradient: "from-pink-300 to-green-300" },
      { name: "More Detailed Line Art", gradient: "from-pink-400 to-orange-300" },
      { name: "Flat Vector Illustration", gradient: "from-orange-400 to-pink-400" },
      { name: "Graphical", gradient: "from-orange-500 to-teal-500" },
      { name: "UPA", gradient: "from-red-400 to-yellow-400" },
      { name: "Minimalist Logo", gradient: "from-teal-400 to-orange-400" },
      { name: "Logo Mascot", gradient: "from-orange-500 to-gray-500" },
      { name: "Die Cut Sticker", gradient: "from-orange-400 to-pink-400" },
      { name: "Minimal Illustration", gradient: "from-gray-300 to-gray-500" },
      { name: "Vintage Drawing", gradient: "from-amber-400 to-gray-500" },
      { name: "Coloring Book", gradient: "from-gray-200 to-gray-400" },
      { name: "T-shirt Design", gradient: "from-teal-400 to-orange-400" },
      { name: "Graffiti", gradient: "from-purple-500 to-pink-500" },
      { name: "Children Book #1", gradient: "from-blue-400 to-pink-400" },
      { name: "Children Book #2", gradient: "from-sky-400 to-green-400" },
      { name: "Children Book #3", gradient: "from-green-400 to-amber-400" },
      { name: "Children Book #4", gradient: "from-yellow-400 to-green-400" },
      { name: "Flat Illustration", gradient: "from-yellow-400 to-orange-400" },
      { name: "American Vintage Illustration", gradient: "from-amber-500 to-gray-500" },
      { name: "Chinese Vintage Poster", gradient: "from-red-500 to-yellow-500" },
      { name: "Soviet Vintage Poster", gradient: "from-green-600 to-amber-500" },
      { name: "50s Illustration", gradient: "from-teal-400 to-pink-400" },
      { name: "Pulp Covers", gradient: "from-orange-500 to-yellow-500" },
      { name: "Character Design Sheet", gradient: "from-blue-500 to-purple-500" },
    ],
  },
};

interface Project {
  id: string;
  title: string;
  script: string | null;
  platform: string | null;
}

// Queue for auto-generating missing previews
let generationQueue: { styleName: string; category: string; callback: () => void }[] = [];
let isProcessingQueue = false;

const processQueue = async () => {
  if (isProcessingQueue || generationQueue.length === 0) return;
  isProcessingQueue = true;
  
  while (generationQueue.length > 0) {
    const item = generationQueue.shift();
    if (item) {
      item.callback();
      // Wait 2 seconds between generations to avoid rate limits
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  }
  
  isProcessingQueue = false;
};

// Style preview image component with lazy loading and auto-generation
const StylePreviewImage = ({ 
  styleName, 
  gradient, 
  category,
  isSelected 
}: { 
  styleName: string; 
  gradient: string; 
  category: string;
  isSelected: boolean;
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isQueued, setIsQueued] = useState(false);
  const { markLoaded } = useContext(ProgressContext);

  const generatePreview = useCallback(async () => {
    if (isGenerating) return;
    
    setIsGenerating(true);
    setIsLoading(true);
    setIsQueued(false);
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-style-preview', {
        body: { styleName, category }
      });
      
      if (error) throw error;
      
      if (data?.imageUrl) {
        setImageUrl(data.imageUrl);
        setHasError(false);
        markLoaded(styleName);
      } else {
        throw new Error('No image URL returned');
      }
    } catch (error) {
      console.error('Failed to generate preview:', error);
      // Re-queue for retry after a delay
      setTimeout(() => {
        setIsQueued(true);
        generationQueue.push({
          styleName,
          category,
          callback: () => generatePreview()
        });
        processQueue();
      }, 5000); // Wait 5 seconds before re-queuing
    } finally {
      setIsLoading(false);
      setIsGenerating(false);
    }
  }, [styleName, category, isGenerating, markLoaded]);

  // Try to load from storage first, auto-queue generation if missing
  useEffect(() => {
    const storageUrl = getStylePreviewUrl(styleName);
    const img = new window.Image();
    img.onload = () => {
      setImageUrl(storageUrl);
      setHasError(false);
      markLoaded(styleName);
    };
    img.onerror = () => {
      setHasError(true);
      // Auto-queue generation for missing images
      setIsQueued(true);
      generationQueue.push({
        styleName,
        category,
        callback: () => generatePreview()
      });
      processQueue();
    };
    img.src = storageUrl;
  }, [styleName, category, generatePreview, markLoaded]);

  if (hasError && !imageUrl) {
    return (
      <div 
        className={`relative aspect-square rounded-lg overflow-hidden mb-2 transition-transform group-hover/style:scale-[1.02] ${getPlaceholderGradient(gradient)}`}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/20">
          {isGenerating || isQueued ? (
            <>
              <Loader2 className="w-4 h-4 text-white animate-spin" />
              <span className="text-[10px] text-white/70">
                {isGenerating ? 'Generating...' : 'Queued'}
              </span>
            </>
          ) : (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  generatePreview();
                }}
                className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                disabled={isGenerating}
              >
                <RefreshCw className="w-4 h-4 text-white" />
              </button>
              <span className="text-[10px] text-white/70">Generate</span>
            </>
          )}
        </div>
        {isSelected && (
          <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
            <Check className="w-3 h-3 text-primary-foreground" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative aspect-square rounded-lg overflow-hidden mb-2 transition-transform group-hover/style:scale-[1.02]">
      {isLoading && (
        <div className={`absolute inset-0 flex items-center justify-center ${getPlaceholderGradient(gradient)}`}>
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
      )}
      {imageUrl && (
        <img 
          src={imageUrl} 
          alt={styleName} 
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setHasError(true)}
        />
      )}
      {isSelected && (
        <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
          <Check className="w-3 h-3 text-primary-foreground" />
        </div>
      )}
    </div>
  );
};

// Calculate total styles count
const TOTAL_STYLES = Object.values(STYLE_CATEGORIES).reduce(
  (acc, cat) => acc + cat.styles.length, 0
);

export default function NexisVisualStyle() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [promptType, setPromptType] = useState<"image" | "video">("image");
  const [platform, setPlatform] = useState("");
  const [script, setScript] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStyle, setSelectedStyle] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  
  // Search and filter
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  
  // Progress tracking
  const [loadedStyles, setLoadedStyles] = useState<Set<string>>(new Set());
  const [showCelebration, setShowCelebration] = useState(false);
  
  const markLoaded = useCallback((styleName: string) => {
    setLoadedStyles(prev => {
      const newSet = new Set(prev);
      newSet.add(styleName);
      return newSet;
    });
  }, []);
  
  // Show celebration when all loaded
  useEffect(() => {
    if (loadedStyles.size === TOTAL_STYLES && !showCelebration) {
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 3000);
    }
  }, [loadedStyles.size, showCelebration]);
  
  const progressValue = {
    loadedCount: loadedStyles.size,
    totalCount: TOTAL_STYLES,
    markLoaded,
  };
  
  // Filter categories and styles based on search and filter
  const filteredCategories = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    
    return Object.entries(STYLE_CATEGORIES)
      .filter(([category]) => filterCategory === "all" || category === filterCategory)
      .map(([category, data]) => {
        const filteredStyles = query
          ? data.styles.filter(style => 
              style.name.toLowerCase().includes(query) || 
              category.toLowerCase().includes(query)
            )
          : data.styles;
        
        return { category, ...data, styles: filteredStyles };
      })
      .filter(cat => cat.styles.length > 0);
  }, [searchQuery, filterCategory]);

  useEffect(() => {
    if (projectId) {
      fetchProject();
    }
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const { data, error } = await supabase
        .from("nexis_projects")
        .select("id, title, script, platform")
        .eq("id", projectId)
        .single();

      if (error) throw error;
      
      setProject(data);
      setScript(data.script || "");
      setPlatform(data.platform || "");
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project");
      navigate("/app/nexis-ai");
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const selectStyle = (category: string, styleName: string) => {
    setSelectedCategory(category);
    setSelectedStyle(styleName);
  };

  const handleContinue = async () => {
    if (!selectedStyle || !platform) {
      toast.error("Please select a visual style and platform");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("nexis_projects")
        .update({
          prompt_type: promptType,
          platform,
          visual_style: selectedStyle,
          visual_style_category: selectedCategory,
          last_step: "scene-count",
        })
        .eq("id", projectId);

      if (error) throw error;
      
      navigate(`/app/nexis-ai/project/${projectId}/scene-count`);
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Get the selected style's gradient for the preview
  const getSelectedStyleGradient = () => {
    if (!selectedCategory || !selectedStyle) return "from-gray-500 to-gray-700";
    const category = STYLE_CATEGORIES[selectedCategory];
    if (!category) return "from-gray-500 to-gray-700";
    const style = category.styles.find(s => s.name === selectedStyle);
    return style?.gradient || "from-gray-500 to-gray-700";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <NexisBreadcrumb projectTitle={project?.title} currentStep="Visual Style" />
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <img src={nexisLogo} alt="Nexis AI" className="w-10 h-10 object-contain" />
          <div>
            <h1 className="font-display text-2xl font-bold">Analyze Script</h1>
            <p className="text-sm text-muted-foreground">{project?.title}</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Prompt Type */}
          <Card className="p-6 bg-card/50 border-border/50">
            <Label className="text-base font-semibold mb-4 block">What type of prompts do you want?</Label>
            <RadioGroup
              value={promptType}
              onValueChange={(v) => setPromptType(v as "image" | "video")}
              className="grid grid-cols-2 gap-4"
            >
              <Label
                htmlFor="image-prompts"
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  promptType === "image" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                }`}
              >
                <RadioGroupItem value="image" id="image-prompts" />
                <Image className="w-6 h-6 text-primary" />
                <span className="font-medium">Image Prompts</span>
              </Label>
              <Label
                htmlFor="video-prompts"
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  promptType === "video" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                }`}
              >
                <RadioGroupItem value="video" id="video-prompts" />
                <Video className="w-6 h-6 text-purple-400" />
                <span className="font-medium">Video Prompts</span>
              </Label>
            </RadioGroup>
          </Card>

          {/* Platform */}
          <Card className="p-6 bg-card/50 border-border/50">
            <Label className="text-base font-semibold mb-4 block">Platform</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger>
                <SelectValue placeholder="Select platform" />
              </SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Card>

          {/* Script Preview */}
          <Card className="p-6 bg-card/50 border-border/50">
            <Label className="text-base font-semibold mb-4 block">Script (auto-filled)</Label>
            <Textarea
              value={script}
              onChange={(e) => setScript(e.target.value)}
              className="min-h-[150px] font-mono text-sm"
              placeholder="Your script will appear here..."
            />
          </Card>

          {/* Visual Style Selector */}
          <Card className="p-6 bg-card/50 border-border/50">
            <div className="flex items-center justify-between mb-4">
              <Label className="text-base font-semibold">Visual Style</Label>
              {loadedStyles.size < TOTAL_STYLES ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Loading previews... {loadedStyles.size}/{TOTAL_STYLES}</span>
                </div>
              ) : showCelebration ? (
                <div className="flex items-center gap-2 text-xs text-primary animate-fade-in">
                  <PartyPopper className="w-4 h-4" />
                  <span className="font-medium">All previews loaded!</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-green-500">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>All {TOTAL_STYLES} styles ready</span>
                </div>
              )}
            </div>
            
            {/* Progress Bar / Complete State */}
            {loadedStyles.size < TOTAL_STYLES ? (
              <div className="mb-4">
                <Progress 
                  value={(loadedStyles.size / TOTAL_STYLES) * 100} 
                  className="h-2"
                />
              </div>
            ) : showCelebration && (
              <div className="mb-4 p-3 rounded-lg bg-gradient-to-r from-primary/20 via-purple-500/20 to-pink-500/20 border border-primary/30 flex items-center gap-3 animate-fade-in">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-lg animate-bounce" style={{ animationDelay: `${i * 100}ms` }}>
                      🎉
                    </span>
                  ))}
                </div>
                <span className="text-sm font-medium text-primary">All style previews generated successfully!</span>
              </div>
            )}
            
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search styles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {Object.keys(STYLE_CATEGORIES).map((category) => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedStyle && (
              <div className="mb-4 p-4 rounded-xl bg-gradient-to-r border border-primary/30 flex items-center gap-4"
                style={{
                  background: `linear-gradient(135deg, hsl(var(--primary) / 0.1), hsl(var(--primary) / 0.05))`,
                }}
              >
                <div className="relative w-20 h-14 rounded-lg overflow-hidden border border-primary/20">
                  <StylePreviewImage 
                    styleName={selectedStyle} 
                    gradient={getSelectedStyleGradient()} 
                    category={selectedCategory}
                    isSelected={false}
                  />
                </div>
                <div className="flex-1">
                  <span className="text-sm text-muted-foreground">{selectedCategory}</span>
                  <p className="font-semibold text-primary">{selectedStyle}</p>
                </div>
                <Check className="w-6 h-6 text-primary" />
              </div>
            )}

            <ProgressContext.Provider value={progressValue}>
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
              {filteredCategories.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No styles found for "{searchQuery}"</p>
                </div>
              ) : (
                filteredCategories.map(({ category, icon: Icon, gradient, styles }) => (
                  <Collapsible
                    key={category}
                    open={expandedCategories.includes(category) || searchQuery.length > 0}
                    onOpenChange={() => toggleCategory(category)}
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-full justify-between p-4 h-auto text-left hover:bg-muted/50 group"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <span className="font-medium">{category}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-muted-foreground">{styles.length} styles</span>
                          {expandedCategories.includes(category) || searchQuery.length > 0 ? (
                            <ChevronDown className="w-4 h-4 transition-transform" />
                          ) : (
                            <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                          )}
                        </div>
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-3 pl-4">
                        {styles.map((style) => (
                          <button
                            key={style.name}
                            onClick={() => selectStyle(category, style.name)}
                            className={`group/style relative p-2 rounded-xl text-left transition-all border-2 ${
                              selectedStyle === style.name && selectedCategory === category
                                ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                                : "border-border/50 hover:border-primary/50 bg-card/30 hover:bg-card/50"
                            }`}
                          >
                            <StylePreviewImage 
                              styleName={style.name} 
                              gradient={style.gradient} 
                              category={category}
                              isSelected={selectedStyle === style.name && selectedCategory === category}
                            />
                            <p className="text-xs font-medium truncate px-1">{style.name}</p>
                          </button>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))
              )}
            </div>
            </ProgressContext.Provider>
          </Card>

          <Button
            variant="hero"
            size="lg"
            className="w-full gap-2"
            onClick={handleContinue}
            disabled={!selectedStyle || !platform || saving}
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Continue
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
